import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';

import { Field, Input, Select, Textarea } from '../lumen';
import { copyProject as apiCopyProject } from '../utils/ApiUtilsV5';
import { ActionModal } from './ActionModal';

export const CopyProjectModal = ({ project, workspaces, onClose, onCopied }) => {
  // Compute both derived values together so we only filter `workspaces` once.
  const { writableWorkspaces, defaultWorkspaceId } = useMemo(() => {
    const writable = workspaces.filter(w => w.permissions?.includes('write'));
    const currentIsWritable = writable.find(
      w => w.workspace_id === project.workspace_id
    );
    const defaultId =
      currentIsWritable?.workspace_id || writable[0]?.workspace_id || '';
    return { writableWorkspaces: writable, defaultWorkspaceId: defaultId };
  }, [workspaces, project.workspace_id]);

  const [name, setName] = useState(`Copy of ${project.name || ''}`);
  const [description, setDescription] = useState(project.description || '');
  const [targetWorkspaceId, setTargetWorkspaceId] = useState(defaultWorkspaceId);

  const trimmedName = name.trim();

  const handleSubmit = () =>
    apiCopyProject({
      projectId: project.project_id,
      name: trimmedName,
      description: description.trim(),
      workspaceId: targetWorkspaceId
    }).then(() => {
      onCopied?.();
      onClose();
    });

  return (
    <ActionModal
      title="Copy project"
      submitLabel="Copy"
      submittingLabel="Copying…"
      fallbackErrorMessage="Can't copy project"
      disabled={!trimmedName || !targetWorkspaceId}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <FormGrid>
        <Field label="Name" htmlFor="copy-project-name">
          <Input
            id="copy-project-name"
            aria-label="Project name"
            value={name}
            autoFocus
            onChange={event => setName(event.target.value)}
          />
        </Field>
        <Field label="Description" htmlFor="copy-project-description">
          <Textarea
            id="copy-project-description"
            aria-label="Project description"
            value={description}
            onChange={event => setDescription(event.target.value)}
          />
        </Field>
        <Field label="Workspace" htmlFor="copy-project-workspace">
          <Select
            id="copy-project-workspace"
            aria-label="Target workspace"
            value={targetWorkspaceId}
            onChange={event => setTargetWorkspaceId(event.target.value)}
            disabled={writableWorkspaces.length < 2}
          >
            {writableWorkspaces.map(ws => (
              <option key={ws.workspace_id} value={ws.workspace_id}>
                {ws.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormGrid>
    </ActionModal>
  );
};

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
